import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  X,
  Save,
  Calendar as CalendarIcon,
  Clock,
  User,
} from 'lucide-react';
import { format, addMinutes, parse } from 'date-fns';
import { ja } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface ChangeHistoryItem {
  timestamp: string;
  action?: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  userId?: string;
  changes?: {
    date?: string;
    startTime?: string;
    duration?: number;
    staff?: string;
    service?: string;
  };
}

interface Reservation {
  id: string;
  customerId: number;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  staffId: string;
  staffName: string;
  serviceId: string;
  serviceName: string;
  date: Date;
  startTime: string;
  endTime: string;
  duration: number;
  status: 'confirmed' | 'pending' | 'cancelled';
  notes?: string;
  color: string;
  changeHistory?: ChangeHistoryItem[];
  isVip?: boolean;
  price?: number;
}

interface ReservationEditModalProps {
  open: boolean;
  reservation: Reservation | null;
  onClose: () => void;
  onSave: (updatedReservation: Reservation) => void;
}

// モックスタッフデータ
const mockStaff = [
  { id: 'staff1', name: '山田 太郎', color: '#4CAF50' },
  { id: 'staff2', name: '佐藤 花子', color: '#2196F3' },
  { id: 'staff3', name: '田中 次郎', color: '#FF9800' },
  { id: 'staff4', name: '鈴木 美香', color: '#9C27B0' },
];

// モックサービスデータ
const mockServices = [
  { id: 'service1', name: 'ドライヘッドスパ 60分', duration: 60, price: 8000 },
  { id: 'service2', name: 'ドライヘッドスパ 90分', duration: 90, price: 12000 },
  { id: 'service3', name: 'フェイシャル', duration: 75, price: 10000 },
  { id: 'service4', name: 'ボディマッサージ', duration: 120, price: 15000 },
];

// 時刻スロット生成（9:00-21:00、15分刻み）
const generateTimeSlots = (): string[] => {
  const slots: string[] = [];
  const startHour = 9;
  const endHour = 21;

  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      slots.push(time);
    }
  }
  return slots;
};

const timeSlots = generateTimeSlots();

const ReservationEditModal: React.FC<ReservationEditModalProps> = ({
  open,
  reservation,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    date: '',
    startTime: '',
    duration: 60,
    staffId: '',
    staffName: '',
    serviceId: '',
    serviceName: '',
    notes: '',
    price: 0,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  // 予約データからフォームデータを初期化
  useEffect(() => {
    if (reservation && open) {
      setFormData({
        date: format(reservation.date, 'yyyy-MM-dd'),
        startTime: reservation.startTime,
        duration: reservation.duration,
        staffId: reservation.staffId,
        staffName: reservation.staffName,
        serviceId: reservation.serviceId,
        serviceName: reservation.serviceName,
        notes: reservation.notes || '',
        price: reservation.price || 0,
      });
      setErrors({});
    }
  }, [reservation, open]);

  // バリデーション
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) {
      newErrors.date = '日付を選択してください';
    }

    if (!formData.startTime) {
      newErrors.startTime = '開始時間を入力してください';
    }

    if (!formData.staffId) {
      newErrors.staffId = 'スタッフを選択してください';
    }

    if (!formData.serviceId) {
      newErrors.serviceId = 'サービスを選択してください';
    }

    if (formData.duration < 15 || formData.duration > 240) {
      newErrors.duration = '施術時間は15分～240分で入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 終了時間計算
  const calculateEndTime = (startTime: string, duration: number) => {
    try {
      const start = parse(startTime, 'HH:mm', new Date());
      const end = addMinutes(start, duration);
      return format(end, 'HH:mm');
    } catch {
      return '';
    }
  };

  // サービス変更ハンドラ
  const handleServiceChange = (serviceId: string) => {
    const service = mockServices.find(s => s.id === serviceId);
    if (service) {
      setFormData(prev => ({
        ...prev,
        serviceId,
        serviceName: service.name,
        duration: service.duration,
        price: service.price,
      }));
    }
  };

  // スタッフ変更ハンドラ
  const handleStaffChange = (staffId: string) => {
    const staff = mockStaff.find(s => s.id === staffId);
    if (staff) {
      setFormData(prev => ({
        ...prev,
        staffId,
        staffName: staff.name,
      }));
    }
  };

  // 保存ハンドラ
  const handleSave = async () => {
    if (!reservation || !validateForm()) return;

    setIsLoading(true);
    try {
      const updatedReservation: Reservation = {
        ...reservation,
        date: new Date(formData.date),
        startTime: formData.startTime,
        endTime: calculateEndTime(formData.startTime, formData.duration),
        duration: formData.duration,
        staffId: formData.staffId,
        staffName: formData.staffName,
        serviceId: formData.serviceId,
        serviceName: formData.serviceName,
        notes: formData.notes,
        price: formData.price,
        changeHistory: [
          ...(reservation.changeHistory || []),
          {
            timestamp: new Date().toISOString(),
            action: '予約内容変更',
            changes: {
              date: formData.date,
              startTime: formData.startTime,
              duration: formData.duration,
              staff: formData.staffName,
              service: formData.serviceName,
            }
          }
        ]
      };

      // 保存処理（実際のAPIコール）
      await new Promise(resolve => setTimeout(resolve, 500));

      onSave(updatedReservation);
      onClose();
    } catch (error) {
      console.error('予約の更新に失敗しました:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!reservation) return null;

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="text-xl font-bold">
            予約内容編集
          </DialogTitle>
          <DialogDescription>
            予約情報を編集してください
          </DialogDescription>
        </DialogHeader>

        {/* 顧客情報表示 */}
        <div className="mb-4 p-3 bg-muted rounded-md">
          <p className="text-sm font-semibold mb-2">
            顧客情報
          </p>
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">
                {reservation.customerName}
              </p>
              <p className="text-xs text-muted-foreground">
                {reservation.customerPhone} • {reservation.customerEmail}
              </p>
            </div>
          </div>
        </div>

        {/* 編集フォーム */}
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* 日付 */}
            <div className="space-y-2">
              <Label htmlFor="edit-date">
                予約日
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="edit-date"
                    variant="outline"
                    className={cn(
                      "w-full min-h-[44px] justify-start text-left font-normal",
                      !formData.date && "text-muted-foreground",
                      errors.date && "border-red-500"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.date
                      ? format(new Date(formData.date), 'yyyy年MM月dd日（E）', { locale: ja })
                      : "日付を選択"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] overflow-hidden p-0">
                  <Calendar
                    mode="single"
                    selected={formData.date ? new Date(formData.date) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        setFormData({ ...formData, date: format(date, 'yyyy-MM-dd') })
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {errors.date && (
                <p className="text-sm text-red-500">{errors.date}</p>
              )}
            </div>

            {/* 開始時間 */}
            <div className="space-y-2">
              <Label htmlFor="edit-start-time">
                開始時間
              </Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                <Select
                  value={formData.startTime}
                  onValueChange={(time) => setFormData({ ...formData, startTime: time })}
                >
                  <SelectTrigger
                    id="edit-start-time"
                    className={cn("pl-10 min-h-[44px]", errors.startTime && "border-red-500")}
                  >
                    <SelectValue placeholder="時刻を選択" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {errors.startTime ? (
                <p className="text-sm text-red-500">{errors.startTime}</p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  終了予定: {calculateEndTime(formData.startTime, formData.duration)}
                </p>
              )}
            </div>

            {/* スタッフ */}
            <div className="space-y-2">
              <Label htmlFor="edit-staff">
                担当スタッフ
              </Label>
              <Select
                value={formData.staffId}
                onValueChange={handleStaffChange}
              >
                <SelectTrigger
                  id="edit-staff"
                  className={cn("min-h-[44px]", errors.staffId && "border-red-500")}
                >
                  <SelectValue placeholder="スタッフを選択" />
                </SelectTrigger>
                <SelectContent>
                  {mockStaff.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: staff.color }}
                        />
                        <span>{staff.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.staffId && (
                <p className="text-sm text-red-500">{errors.staffId}</p>
              )}
            </div>

            {/* サービス */}
            <div className="space-y-2">
              <Label htmlFor="edit-service">
                サービス
              </Label>
              <Select
                value={formData.serviceId}
                onValueChange={handleServiceChange}
              >
                <SelectTrigger
                  id="edit-service"
                  className={cn("min-h-[44px]", errors.serviceId && "border-red-500")}
                >
                  <SelectValue placeholder="サービスを選択" />
                </SelectTrigger>
                <SelectContent>
                  {mockServices.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.serviceId && (
                <p className="text-sm text-red-500">{errors.serviceId}</p>
              )}
            </div>

            {/* 施術時間 */}
            <div className="space-y-2">
              <Label htmlFor="edit-duration">
                施術時間（分）
              </Label>
              <Input
                id="edit-duration"
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                min={15}
                max={240}
                step={15}
                className={cn("min-h-[44px]", errors.duration && "border-red-500")}
              />
              {errors.duration && (
                <p className="text-sm text-red-500">{errors.duration}</p>
              )}
            </div>

            {/* 料金 */}
            <div className="space-y-2">
              <Label htmlFor="edit-price">
                料金（円）
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">¥</span>
                <Input
                  id="edit-price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                  min={0}
                  step={100}
                  className="pl-8 min-h-[44px]"
                />
              </div>
            </div>
          </div>

          {/* メモ */}
          <div className="space-y-2">
            <Label htmlFor="edit-notes">
              予約メモ
            </Label>
            <Textarea
              id="edit-notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="特別な要望やメモがあれば入力してください"
              maxLength={500}
              className="min-h-[88px] resize-none"
              rows={3}
            />
            <p className="text-xs text-muted-foreground text-right">
              {formData.notes.length}/500文字
            </p>
          </div>
        </div>

        <DialogFooter className="border-t pt-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="min-h-[44px]"
          >
            <X className="mr-2 h-4 w-4" />
            キャンセル
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
            className="min-h-[44px]"
          >
            <Save className="mr-2 h-4 w-4" />
            {isLoading ? '保存中...' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReservationEditModal;
